import {useCallback, useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {runPreviewAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {Chart} from 'charts/ChartTypes';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {ErrorHandlingOptions} from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {PreviewOutput} from 'pipelines/PipelineDAO';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncState, AsyncStatus, NOT_FETCHED_ASYNC_STATE} from 'ui-library/hooks/useAsyncState';
import usePrevious from 'ui-library/hooks/usePrevious';

const previewAsyncGlobalState =
  makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType.CHART_VIEW>>>(NOT_FETCHED_ASYNC_STATE);
const previewInputDataFrameState = makeUseGlobalClientState<string | null>(null);

export interface UseChartViewPreviewOutput {
  chart: Chart | null;
  chartError: ErrorHandlingOptions | null;
  dataFrame: DataFrame;
  isLoading: boolean;
  invalidatePreview: () => void;
}

export default function useChartViewPreview(
  initialChart: Chart | null,
  initialDataFrame: DataFrame,
  inputDataFrameId: string | null,
  clientStateId: string,
  visualizationConfig: VisualizationConfig | null,
  skipPreview: boolean
): UseChartViewPreviewOutput {
  const [chartPreviewState, setChartPreviewState] =
    previewAsyncGlobalState.useGlobalClientState(clientStateId);
  const [inputDataFrameIdOfPreview, setInputDataFrameIdOfPreview] =
    previewInputDataFrameState.useGlobalClientState(clientStateId);
  const previousVisualizationConfig = usePrevious(visualizationConfig);
  const previousInputDataFrameId = usePrevious(inputDataFrameId);

  /* This is useful, e.g. when we update and run a step that may also generate previews as is the case with
   * REGRESSION steps. For example:
   * - Open succeeded REGRESSION step
   * - Change x, y axes (generates preview)
   * - Change REGRESSION options and apply (updates and runs step)
   * - Invalidate the generated preview, or else we will not pick up the new chart from the REGRESSION step
   *   output
   */
  const invalidatePreview = useCallback(() => {
    setChartPreviewState(NOT_FETCHED_ASYNC_STATE);
  }, [setChartPreviewState]);

  const queryChartPreview = useCallback(
    (inputDataFrameId: string, visualizationConfig: VisualizationConfig, wasPreviouslyTable: boolean) => {
      runPreviewAnalysisStep(
        {
          stepType: PipelineStepType.CHART_VIEW,
          stepConfig: {
            chartConfig: getPersistedChartViewConfig(visualizationConfig),
            chartName: 'preview chart',
          },
          stepInput: {
            inputDataFrameId,
          },
        },
        chartPreviewState,
        (nextState: AsyncState<PreviewOutput<PipelineStepType.CHART_VIEW>>): void => {
          // Clear previous data if we are requesting a new preview and previous visualization type was a table
          // because other wise we may display an underlay that depicts a previous preview.
          // E.g.
          // - Edit PIE_CHART to SCATTER_PLOT (PIE_CHART underlay)
          // - Edit to TABLE (no preview, no underlay)
          // - Edit to BAR_CHART (SCATTER_PLOT underlay unless we reset here)
          if (nextState.status === AsyncStatus.LOADING && wasPreviouslyTable) {
            setChartPreviewState({...nextState, previousData: null});
          } else {
            setChartPreviewState(nextState);
          }
          setInputDataFrameIdOfPreview(inputDataFrameId);
        }
      );
    },
    [chartPreviewState, setChartPreviewState, setInputDataFrameIdOfPreview]
  );

  useEffect(() => {
    // Query only if step type is CHART_VIEW or TABLE_VIEW, we have input data and configuration, and config has changed
    // from the initial value or there is no persisted chart (this may be the case if we are converting a table
    // to a chart).
    if (
      !skipPreview &&
      inputDataFrameId != null &&
      visualizationConfig != null &&
      getVisualizationType(visualizationConfig) !== VisualizationType.TABLE &&
      ((previousVisualizationConfig != null &&
        !isEqual(previousVisualizationConfig.parameters, visualizationConfig.parameters)) ||
        (inputDataFrameIdOfPreview != null && inputDataFrameIdOfPreview !== inputDataFrameId) ||
        (initialChart == null && chartPreviewState === NOT_FETCHED_ASYNC_STATE))
    ) {
      queryChartPreview(
        inputDataFrameId,
        visualizationConfig,
        previousVisualizationConfig != null &&
          getVisualizationType(previousVisualizationConfig) === VisualizationType.TABLE
      );
    }
  }, [
    initialChart,
    previousVisualizationConfig,
    visualizationConfig,
    queryChartPreview,
    skipPreview,
    inputDataFrameId,
    inputDataFrameIdOfPreview,
    chartPreviewState,
    previousInputDataFrameId,
  ]);

  // Ignore any chartPreviewState if current visualization is a table, because tables never use the preview
  if (visualizationConfig != null && getVisualizationType(visualizationConfig) !== VisualizationType.TABLE) {
    // If preview was for a previous input data frame, then do not return it, as it is outdated. This happens,
    // for example, if an upsteam transform changes after a preview is generated
    if (chartPreviewState.status === AsyncStatus.SUCCESS) {
      return {
        chart: chartPreviewState.data.charts![SINGLE_CHART_OUTPUT_STEP_DATA_NAME][0],
        chartError: null,
        dataFrame: chartPreviewState.data.dataFrames[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME][0],
        invalidatePreview,
        isLoading: false,
      };
    } else if (chartPreviewState.status === AsyncStatus.LOADING) {
      return {
        chart: chartPreviewState.previousData?.charts![SINGLE_CHART_OUTPUT_STEP_DATA_NAME][0] ?? null,
        chartError: null,
        dataFrame:
          chartPreviewState.previousData?.dataFrames![SINGLE_DATASET_OUTPUT_STEP_DATA_NAME][0] ??
          initialDataFrame,
        invalidatePreview,
        isLoading: true,
      };
    } else if (chartPreviewState.status === AsyncStatus.ERROR) {
      return {
        chart: null,
        chartError: {
          errorMessage: chartPreviewState.responseJSON.responseText,
          showVisualization: false,
        },
        dataFrame: initialDataFrame,
        invalidatePreview,
        isLoading: false,
      };
    }
  }

  return {
    chart: initialChart ?? null,
    chartError: null,
    dataFrame: initialDataFrame,
    invalidatePreview,
    isLoading: false,
  };
}
